// Service Worker Registration
// Handles registration, update detection, and controlled reload on new versions.

const isLocalhost = Boolean(
    window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

type Config = {
    onSuccess?: (registration: ServiceWorkerRegistration) => void;
    onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Tell a waiting service worker to call skipWaiting().
 * After the new SW activates the `controllerchange` listener (installed below)
 * will reload the page automatically.
 */
export function applyUpdate(registration: ServiceWorkerRegistration): void {
    const waiting = registration.waiting;
    if (waiting) {
        waiting.postMessage({ type: 'SKIP_WAITING' });
    }
}

/**
 * Register the service worker and set up smart update detection.
 */
export function register(config?: Config): void {
    if ('serviceWorker' in navigator) {
        const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
        if (publicUrl.origin !== window.location.origin) {
            return;
        }

        window.addEventListener('load', () => {
            const swUrl = `${process.env.PUBLIC_URL}/serviceworker.js`;

            if (isLocalhost) {
                checkValidServiceWorker(swUrl, config);
                navigator.serviceWorker.ready.then(() => {
                    console.log(
                        'This web app is being served cache-first by a service worker. ' +
                        'To learn more, visit https://cra.link/PWA'
                    );
                });
            } else {
                registerValidSW(swUrl, config);
            }
        });

        // ── Reload once when a new SW takes control ──────────────────────
        let isReloading = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            if (isReloading) return;
            isReloading = true;
            console.log('[SW] New service worker activated — reloading page.');
            window.location.reload();
        });
    }
}

export function unregister(): void {
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready
            .then((registration) => registration.unregister())
            .catch((error) => console.error(error.message));
    }
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

const MILLISECONDS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const UPDATE_POLL_INTERVAL_MINUTES = 45;
const NOT_FOUND_STATUS = 404;

/** How often (ms) to poll for updates as a fallback. */
const UPDATE_POLL_INTERVAL_MS = UPDATE_POLL_INTERVAL_MINUTES * SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND;

/**
 * Attach smart update-check triggers to a registration:
 *  • when the page becomes visible again (visibilitychange)
 *  • when the window regains focus
 *  • when the browser comes back online
 *  • long-interval fallback poll (45 min)
 *
 * Each trigger is debounced so rapid events don't spam the server.
 */
function attachUpdateTriggers(registration: ServiceWorkerRegistration): void {
    let lastCheckTime = Date.now();
    const MIN_CHECK_INTERVAL_MS = SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND;

    const checkForUpdate = () => {
        const now = Date.now();
        if (now - lastCheckTime < MIN_CHECK_INTERVAL_MS) return;
        lastCheckTime = now;
        registration.update().catch((err) => {
            console.warn('[SW] Update check failed:', err);
        });
    };

    // Tab becomes visible
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            checkForUpdate();
        }
    });

    // Window gains focus
    window.addEventListener('focus', checkForUpdate);

    // Browser comes back online
    window.addEventListener('online', checkForUpdate);

    // Long fallback interval
    setInterval(checkForUpdate, UPDATE_POLL_INTERVAL_MS);
}

/**
 * Handle the lifecycle of a newly-found installing worker.
 */
function trackInstallingWorker(
    registration: ServiceWorkerRegistration,
    installingWorker: ServiceWorker,
    config?: Config
): void {
    installingWorker.onstatechange = () => {
        if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
                // New content available — there is already a previous SW controlling.
                console.log('[SW] New content is available; update ready.');
                config?.onUpdate?.(registration);
            } else {
                // First-time install — no previous SW was controlling.
                console.log('[SW] Content is cached for offline use.');
                config?.onSuccess?.(registration);
            }
        }
    };
}

function registerValidSW(swUrl: string, config?: Config): void {
    navigator.serviceWorker
        .register(swUrl)
        .then((registration) => {
            // ── If an update is already waiting, notify immediately ───────
            if (registration.waiting) {
                console.log('[SW] Update already waiting.');
                config?.onUpdate?.(registration);
            }

            // ── Listen for future updates ────────────────────────────────
            registration.onupdatefound = () => {
                const installingWorker = registration.installing;
                if (installingWorker == null) return;
                trackInstallingWorker(registration, installingWorker, config);
            };

            // ── Attach smart update triggers ─────────────────────────────
            attachUpdateTriggers(registration);
        })
        .catch((error) => {
            console.error('[SW] Error during service worker registration:', error);
        });
}

function checkValidServiceWorker(swUrl: string, config?: Config): void {
    fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
        .then((response) => {
            const contentType = response.headers.get('content-type');
            if (
                response.status === NOT_FOUND_STATUS ||
                (contentType != null && contentType.indexOf('javascript') === -1)
            ) {
                // No service worker found — unregister and reload.
                navigator.serviceWorker.ready.then((registration) => {
                    registration.unregister().then(() => window.location.reload());
                });
            } else {
                registerValidSW(swUrl, config);
            }
        })
        .catch(() => {
            console.log('[SW] No internet connection found. App is running in offline mode.');
        });
}

